import { buildAttributeConstraints } from "../services/pasta";
import { callBFF } from "../services/BFFService";

import { hasConstrainedOptionValues } from "../utils/ProductBuilderHelper";
import { isMailingServicesMpv, mailingServiceQuantities } from "../utils/mailingServicesUtil";
import { bulkOrderBigQuantity } from "../utils/bulkOrder";
import { MpvProvider } from "../utils/MpvProvider";
// @ts-ignore
import { ensureUserTestId } from "@vp/ab-test-cookie";

import { MerchandisingProductView } from "../types/ContentfulTypes";
import { AttributeConstraint } from "../types/ConstraintsTypes";
import RangeType from "../types/RangeTypes";
import { BffDebugData, BFFResponse } from "../types/BFFServiceTypes";

export interface UpdatedState extends BFFResponse {
  selectedQuantity: number;
  attributeConstraints?: AttributeConstraint[];
  selections: Record<string, string>;
  mpv?: MerchandisingProductView;
  debugData?: BffDebugData;
}

export const getUpdatedState = async ({
  locale,
  currentCompatibleValues,
  currentCompatibleRanges,
  selectedQuantity,
  mpv,
  selections,
  pricingContext,
  isDebug,
}: {
  locale: string;
  currentCompatibleValues: Record<string, string[]>;
  currentCompatibleRanges: Record<string, RangeType[]>;
  selectedQuantity: number;
  mpv: MerchandisingProductView;
  selections: Record<string, string>;
  pricingContext?: string;
  isDebug?: boolean;
}): Promise<UpdatedState> => {
  const mpvProvider = new MpvProvider(mpv);
  const productPageOptions = mpvProvider.getOptionKeys();

  let rangedQuantityList: number[] = [];
  if (isMailingServicesMpv(mpv.mpvId, locale)) {
    rangedQuantityList = mailingServiceQuantities.quantities;
  }

  let bffData = await callBFF({
    locale,
    productKey: mpv.coreProductId,
    selections,
    pricingContext,
    productPageOptions,
    version: mpv.version,
    quantities: rangedQuantityList,
    currentQuantity: selectedQuantity,
    optimizelyEndUserId: ensureUserTestId(),
    isDebug,
  });

  if (!bffData) {
    bffData = {
      prices: {},
      compatibleRanges: currentCompatibleRanges,
      compatibleValues: currentCompatibleValues,
      compatibleQuantities: [],
    };
  }

  const {
    prices,
    compatibleValues,
    compatibleRanges,
    compatibleQuantities,
    productConstraints,
    debug,
    multisiteflow,
  } = bffData;

  let outOfStock;
  let attributeConstraints: AttributeConstraint[] = [];

  if (productConstraints && productConstraints.length) {
    const constraintInformation = buildAttributeConstraints(productConstraints, mpv);
    attributeConstraints = constraintInformation?.attributeConstraints;
    outOfStock = constraintInformation?.outOfStock;
  }

  if (Object.keys(selections).length > 0) {
    const hasConstrainedOption = hasConstrainedOptionValues(selections, attributeConstraints);

    if (hasConstrainedOption) {
      return await getUpdatedState({
        locale,
        currentCompatibleValues,
        currentCompatibleRanges,
        selectedQuantity,
        mpv,
        selections: {},
        isDebug,
      });
    }
  }

  const sortedCompatibleQuantities = compatibleQuantities.sort((a, b) => a - b);
  if (
    !compatibleQuantities.includes(selectedQuantity) &&
    selectedQuantity !== bulkOrderBigQuantity
  ) {
    selectedQuantity = sortedCompatibleQuantities[0];
  }

  const result: UpdatedState = {
    prices,
    compatibleValues,
    compatibleRanges,
    compatibleQuantities: sortedCompatibleQuantities,
    selectedQuantity,
    attributeConstraints,
    temporarilyOutOfStock: outOfStock,
    selections,
    debugData: debug,
    multisiteflow,
  };

  return result;
};
